/**
 * Entitlement Token Service
 *
 * Fetches signed entitlement tokens from the licensing-get-entitlement
 * API endpoint. These tokens are used for offline Pro verification.
 *
 * SERVER-BACKED TRIAL:
 * Trial data is now stored server-side. Use `startServerTrial()` to create
 * a trial, and `fetchEntitlementToken()` to get the current entitlement
 * which includes server-backed trial status.
 */

import { getAccessToken, isAuthConfigured } from './authService';

const API_URL = import.meta.env.VITE_API_URL || '';

/** Result from fetching entitlement token */
export interface EntitlementTokenResult {
  success: boolean;
  /** The signed JWT token */
  token?: string;
  /** Token payload for display (mode, expiry, etc.) */
  payload?: {
    mode: 'personal' | 'trial' | 'pro';
    subscription_status: string;
    trial_expires_at: string | null;
    trial_started_at: string | null;
    trial_days_remaining: number | null;
    trial_is_server_backed: boolean;
    subscription_expires_at: string | null;
    expires_at: string;
  };
  error?: string;
}

/** Result from starting a server-backed trial */
export interface StartTrialResult {
  success: boolean;
  /** The signed JWT token with trial entitlement */
  token?: string;
  /** Trial info */
  trial?: {
    started_at: string;
    expires_at: string;
    is_new: boolean;
    is_active: boolean;
  };
  /** Token payload for display */
  payload?: {
    mode: 'personal' | 'trial' | 'pro';
    subscription_status: string;
    trial_expires_at: string | null;
    trial_days_remaining: number | null;
  };
  /** Message if already has subscription */
  message?: string;
  error?: string;
  /** Error code for specific handling */
  errorCode?: 'DEVICE_COOLDOWN' | string;
  /** Days remaining in device cooldown (if blocked) */
  cooldownDaysRemaining?: number;
}

/**
 * In-flight request deduplication.
 * Multiple callers (authStore, useSignedTokenSync, subscriptionStore) may
 * call fetchEntitlementToken simultaneously — share a single request.
 */
let inflightRequest: Promise<EntitlementTokenResult> | null = null;

/**
 * Fetch a signed entitlement token from the server.
 * Concurrent calls are deduplicated — only one network request is made.
 */
export const fetchEntitlementToken = (): Promise<EntitlementTokenResult> => {
  if (inflightRequest) return inflightRequest;

  inflightRequest = fetchEntitlementTokenImpl().finally(() => {
    inflightRequest = null;
  });

  return inflightRequest;
};

const fetchEntitlementTokenImpl = async (): Promise<EntitlementTokenResult> => {
  if (!API_URL) {
    return { success: false, error: 'API URL is not configured' };
  }

  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      return { success: false, error: 'Not authenticated - no access token' };
    }

    const response = await fetch(`${API_URL}/licensing-get-entitlement`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.error || `Request failed with status ${response.status}` };
    }

    const data = await response.json();
    const { token, payload } = data;

    if (!token) {
      return { success: false, error: 'No token in response' };
    }

    return { success: true, token, payload };
  } catch (err) {
    console.error('[EntitlementToken] Fetch error:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch entitlement token',
    };
  }
};

/**
 * `feature:trial_start_error` — why a trial start failed (the server's
 * structured errorCode like DEVICE_COOLDOWN when present, else a coarse
 * bucket). Fired HERE so every caller (trial dialog, Settings tab) is
 * covered; fills the funnel blind spot where "Start Free Trial" clicks
 * vanished with no trace of the reason. Value is bounded to an
 * uppercase-token shape — never free text.
 */
function trackTrialStartError(errorCode: string | undefined, fallback: string): void {
  const bounded =
    errorCode && /^[A-Z0-9_]{2,40}$/.test(errorCode) ? errorCode : fallback;
  window.electronAPI?.analytics
    ?.track('feature:trial_start_error', { errorCode: bounded })
    ?.catch(() => {});
}

/**
 * Start a server-backed trial for the authenticated user.
 */
export const startServerTrial = async (): Promise<StartTrialResult> => {
  if (!API_URL) {
    trackTrialStartError(undefined, 'NOT_CONFIGURED');
    return { success: false, error: 'API URL is not configured' };
  }

  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      trackTrialStartError(undefined, 'AUTH');
      return { success: false, error: 'Not authenticated - sign in to start trial' };
    }

    // Get device fingerprint from Electron (if available)
    let deviceFingerprint: string | undefined;
    try {
      const deviceInfo = await window.electronAPI?.device?.getInfo?.();
      deviceFingerprint = deviceInfo?.fingerprint;
    } catch (err) {
      console.warn('[StartTrial] Failed to get device fingerprint:', err);
    }

    const response = await fetch(`${API_URL}/licensing-start-trial`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ deviceFingerprint }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      trackTrialStartError(errorData.errorCode, 'SERVER');
      return {
        success: false,
        error: errorData.error || `Request failed with status ${response.status}`,
        errorCode: errorData.errorCode,
        cooldownDaysRemaining: errorData.cooldownDaysRemaining,
      };
    }

    const data = await response.json();
    const { success, token, trial, payload, message, error, errorCode, cooldownDaysRemaining } = data;

    if (!success) {
      trackTrialStartError(errorCode, 'SERVER');
      return {
        success: false,
        error: error || 'Failed to start trial',
        errorCode,
        cooldownDaysRemaining,
      };
    }

    return { success: true, token, trial, payload, message };
  } catch (err) {
    console.error('[StartTrial] Error:', err);
    trackTrialStartError(undefined, 'EXCEPTION');
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to start trial',
    };
  }
};

/** Structured objections the trial-expiry dialog offers (server validates the same set). */
export const TRIAL_EXTENSION_REASONS = [
  'price',
  'missing_feature',
  'still_evaluating',
  'company_wont_pay',
  'other',
] as const;

export type TrialExtensionReason = (typeof TRIAL_EXTENSION_REASONS)[number];

/**
 * The trial-expiry dialog's feedback-for-14-days exchange: submit one
 * structured objection (+ optional free text) and receive an extended trial.
 * Strictly once per account — the server rejects a second attempt with
 * ALREADY_EXTENDED. Response mirrors `startServerTrial` so callers apply it
 * through the same `startTrialFromServer` path.
 *
 * `feature:trial_extension_result` is fired here (success + errorCode-bounded
 * failure) so the exchange's conversion is measurable at its single choke
 * point. Feedback text itself is never sent to analytics.
 */
export const extendServerTrial = async (
  reason: TrialExtensionReason,
  details: string | null
): Promise<StartTrialResult> => {
  const trackResult = (success: boolean, errorCode?: string) => {
    const bounded =
      errorCode && /^[A-Z0-9_]{2,40}$/.test(errorCode) ? errorCode : undefined;
    window.electronAPI?.analytics
      ?.track('feature:trial_extension_result', {
        success,
        ...(success ? {} : { errorCode: bounded ?? 'OTHER' }),
      })
      ?.catch(() => {});
  };

  if (!API_URL) {
    trackResult(false, 'NOT_CONFIGURED');
    return { success: false, error: 'API URL is not configured' };
  }

  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      trackResult(false, 'AUTH');
      return { success: false, error: 'Not authenticated - sign in first' };
    }

    let appVersion: string | undefined;
    try {
      appVersion = (await window.electronAPI?.autoUpdates?.getState?.())?.currentVersion ?? undefined;
    } catch {
      // Version is nice-to-have context on the feedback row only
    }

    const response = await fetch(`${API_URL}/licensing-extend-trial`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ reason, details, appVersion }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      trackResult(false, data.errorCode ?? 'SERVER');
      return {
        success: false,
        error: data.error || `Request failed with status ${response.status}`,
        errorCode: typeof data.errorCode === 'string' ? data.errorCode : undefined,
      };
    }

    trackResult(true);
    return {
      success: true,
      token: data.token,
      trial: data.trial,
      payload: data.payload,
      message: data.message,
    };
  } catch (err) {
    console.error('[ExtendTrial] Error:', err);
    trackResult(false, 'EXCEPTION');
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to extend trial',
    };
  }
};

/**
 * Check if entitlement token service is configured.
 */
export const isEntitlementTokenServiceConfigured = (): boolean => isAuthConfigured() && Boolean(API_URL);
